<script setup lang="ts">
import LogoContainer from './LogoContainer.vue';
import ServiceDestinations from './ServiceDestinations.vue';
import InformationSection from '@components/util/InformationSection.vue';
import FaqList from '@components/util/faq';
import BusyWay from '@components/util/BusyWay.vue';
import RawContentStyleProvider from '@components/base/mr-data-table/RawContentStyleProvider.vue';

const { title, informationPosition = 'top' } = defineProps<{
    title?: string;
    hideBusyWay?: boolean;
    hideTitle?: boolean;
    informationPosition?: 'top' | 'bottom';
}>();
const contentStore = useContentStore();
const {
    landingPageContents,
    landingPageContentLoading,
    landingSections,
    providers,
    providersTitle,
} = classToRefs(contentStore);
const { isWebApp } = useDevice();
</script>

<template>
    <DelayHydration>
        <template v-if="!isWebApp">
            <ServiceDestinations
                v-if="
                    landingPageContents &&
                    !landingPageContentLoading &&
                    landingPageContents.cityName !== ''
                "
            />
            <RawContentStyleProvider>
                <div
                    v-show="!hideTitle && (title || landingPageContents?.body)"
                    class="container landing-content"
                >
                    <h2 v-if="title">{{ title }}</h2>
                    <div
                        v-if="landingPageContents?.body"
                        v-html="landingPageContents.body"
                    />
                </div>
                <template v-if="informationPosition === 'top'">
                    <InformationSection
                        v-for="section in landingSections"
                        :key="section.id"
                        :content="section"
                    />
                </template>
                <slot />
                <div v-if="providers.length" class="wrapper">
                    <div class="logo-container">
                        <div class="container">
                            <h2 class="providers-title">
                                {{ providersTitle }}
                            </h2>
                            <LogoContainer :logos="providers" />
                        </div>
                    </div>
                </div>
            </RawContentStyleProvider>
        </template>
        <RawContentStyleProvider>
            <div v-if="landingPageContents?.faqs?.length" class="container">
                <h2 v-show="!hideTitle">سوالات متداول</h2>
                <FaqList :items="landingPageContents?.faqs || []" />
            </div>
            <template v-if="!isWebApp">
                <template v-if="informationPosition === 'bottom'">
                    <InformationSection
                        v-for="section in landingSections"
                        :key="section.id"
                        :content="section"
                    />
                </template>

                <BusyWay
                    v-show="!hideBusyWay"
                    :page-data="landingPageContents ?? undefined"
                />
            </template>
        </RawContentStyleProvider>
    </DelayHydration>
</template>

<style lang="scss" scoped>
.wrapper {
    background: white;
}

.logo-container {
    padding: 48px 0 106px 0;
}

@media (max-width: $app-mobile-max-width) {
    #second-information .container {
        padding: 48px 16px;
    }

    .providers-title {
        font-style: normal !important;
        @include font-weight(bold, $important: true);
        font-size: 20px !important;
        line-height: 31px !important;
        color: usePalette($p-cool-gray, $p-800) !important;
        margin-bottom: 24px !important;
        text-align: center !important;
    }

    .logo-container {
        padding-bottom: 44px;
    }
}

h2 {
    text-align: center;
}
:deep(.landing-content) {
    padding: 12px 0;
    @include mobile-only(1232px) {
        padding-inline: 16px;
    }
    p,
    li {
        @include tg-m($color: usePalette($p-cool-gray, $p-800));
        text-align: justify;
    }
    figure {
        margin: 0 !important;
    }
    img {
        max-width: 100%;
    }
}
.container {
    width: 100%;
    max-width: 1200px;
    margin-inline: auto;
    @include mobile-only(1232px) {
        padding-inline: 16px;
        margin-inline: 0 !important;
        max-width: none;
    }
}
</style>
